"use client";

import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { Reveal } from "@/components/motion/reveal";

/**
 * Status + Waiting List. Replaces the old direct-download section: the
 * hosted version is held back while funding for hosting infrastructure and
 * ODPC compliance is raised; a fully local, on-device version launches
 * first and stays available afterward too. This section states that
 * plainly, then captures interest.
 *
 * Privacy claim is deliberately scoped to what's actually true: local-only
 * for anything Pisoque Technologies itself would see, with the two real
 * exceptions named rather than glossed over — Sentry crash reports (always,
 * automatic) and the optional AI Assistant (only when the admin triggers it).
 * No license-check network call exists yet (that's a future phase), so it
 * isn't mentioned.
 *
 * Form posts to the Cloudflare Pages Function at /api/waitlist
 * (functions/api/waitlist.ts), which forwards the signup to hello@pisoque.co.ke
 * and fires an autoresponder via Resend — no backend server involved.
 *
 * "Continue with Google" (Google Identity Services button) sits above the
 * plain-email form as the lower-friction option — one click, no typing. It
 * hands back a signed ID token (JWT), verified server-side against Google's
 * tokeninfo endpoint (audience + email_verified checked) before any email is
 * sent — the client never gets to just assert an email address. No-op if
 * NEXT_PUBLIC_GOOGLE_CLIENT_ID isn't set, same pattern as Sentry's DSN.
 */

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

type SubmitState = "idle" | "sending" | "sent" | "already_registered" | "error" | "not_configured";

const BUSINESS_TYPES = [
  { value: "", label: "Business type (optional)" },
  { value: "cyber_cafe", label: "Cyber café" },
  { value: "sme", label: "SME" },
  { value: "other", label: "Other" },
];

export function StatusWaitlist() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [submit, setSubmit] = useState<SubmitState>("idle");
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const businessTypeRef = useRef(businessType);
  businessTypeRef.current = businessType;

  const submitPayload = async (payload: Record<string, string>) => {
    setSubmit("sending");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status === 503) {
        setSubmit("not_configured");
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { alreadyRegistered?: boolean };
      setSubmit(data.alreadyRegistered ? "already_registered" : "sent");
      setEmail("");
      setName("");
      setBusinessType("");
    } catch {
      setSubmit("error");
    }
  };

  const submitSignup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submit === "sending") return;
    submitPayload({ email: email.trim(), name: name.trim(), businessType });
  };

  // Google Identity Services: render the button once the script has loaded
  // and a client ID is configured. The callback fires with a signed JWT —
  // sent straight to the same endpoint, verified server-side there. The
  // current businessType is read via a ref (not a dep) so picking a
  // business type after the button renders doesn't force a re-render that
  // would reset Google's own button state.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleScriptLoaded) return;
    const g = (window as any).google;
    if (!g?.accounts?.id || !googleButtonRef.current) return;

    g.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response: { credential: string }) => {
        submitPayload({ googleCredential: response.credential, businessType: businessTypeRef.current });
      },
    });
    g.accounts.id.renderButton(googleButtonRef.current, {
      theme: "outline",
      size: "large",
      text: "signup_with",
      width: 320,
    });
  }, [googleScriptLoaded]);

  return (
    <section id="download" className="relative mx-auto w-full max-w-6xl px-6 py-20">
      <Reveal>
        <div className="max-w-2xl">
          <span className="mono-label text-accent-text">current status</span>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            We&apos;re holding SHERIFF-AI back — on purpose.
          </h2>
          <p className="mt-4 text-lg text-muted">
            Here&apos;s the honest reason: we&apos;re currently raising the
            funds needed for two things — proper hosting infrastructure, and
            full compliance with Kenya&apos;s Data Protection Act, including
            ODPC registration. Both cost real money, and we&apos;d rather
            raise it properly than cut corners to launch faster.
          </p>
          <p className="mt-4 text-lg text-muted">
            While we do that, the version we launch first will be fully
            local — it runs entirely on your own device and network, nothing
            hosted by us, nothing phoning home. Two honest exceptions: if the
            app crashes, an anonymized crash report goes to our
            error-monitoring vendor so we can fix it; and if you use the
            optional AI Assistant, that one query goes to our AI provider —
            only when you trigger it, never automatically.
          </p>
          <p className="mt-4 text-lg text-muted">
            Once funding is secured and hosting + compliance are both in
            place, we&apos;ll also offer a hosted version — running from our
            own platform, for businesses that would rather not manage
            anything themselves. The local version isn&apos;t a stopgap; it
            stays available after that too. Our core belief driving all of
            this: privacy should be respected, period — that&apos;s why the
            local version exists at all, and why we won&apos;t launch the
            hosted one before compliance is real.
          </p>
        </div>
      </Reveal>

      {GOOGLE_CLIENT_ID && (
        <Script
          src="https://accounts.google.com/gsi/client"
          strategy="afterInteractive"
          onLoad={() => setGoogleScriptLoaded(true)}
        />
      )}

      <Reveal>
        <form
          onSubmit={submitSignup}
          className="glass mt-10 max-w-xl rounded-xl2 p-8"
          aria-label="Join the waiting list"
        >
          <h3 className="text-xl font-bold tracking-tight">Join the waiting list</h3>
          <p className="mt-2 text-base text-muted">
            Get notified the moment SHERIFF-AI is available, and help us gauge
            demand as we finalize timelines.
          </p>

          {GOOGLE_CLIENT_ID && (
            <div className="mt-6">
              <div ref={googleButtonRef} />
              <div className="mt-5 flex items-center gap-3 text-sm text-muted">
                <span className="h-px flex-1 bg-border" aria-hidden />
                or use your email
                <span className="h-px flex-1 bg-border" aria-hidden />
              </div>
            </div>
          )}

          <div className="mt-6 grid gap-4">
            <input
              type="email"
              required
              maxLength={200}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@business.co.ke"
              aria-label="Email address"
              className="glass min-h-[48px] rounded-xl2 px-4 text-base text-text placeholder:text-muted"
            />
            <input
              type="text"
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              aria-label="Your name"
              className="glass min-h-[48px] rounded-xl2 px-4 text-base text-text placeholder:text-muted"
            />
            <select
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              aria-label="Business type"
              className="glass min-h-[48px] rounded-xl2 px-4 text-base text-text"
            >
              {BUSINESS_TYPES.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={submit === "sending"}
              className="neo min-h-[48px] rounded-xl2 bg-accent px-6 text-base font-semibold text-accent-ink disabled:opacity-60"
            >
              {submit === "sending" ? "Joining…" : "Join waiting list"}
            </button>

            {submit === "sent" && (
              <p className="text-base text-accent-text" role="status">
                You&apos;re on the list — check your inbox for confirmation.
              </p>
            )}
            {submit === "already_registered" && (
              <p className="text-base text-accent-text" role="status">
                You&apos;re already on the waiting list with that email — no
                need to sign up again.
              </p>
            )}
            {(submit === "error" || submit === "not_configured") && (
              <p className="text-base" role="alert" style={{ color: "var(--warn)" }}>
                Couldn&apos;t submit right now — email{" "}
                <a href="mailto:hello@pisoque.co.ke" className="underline">
                  hello@pisoque.co.ke
                </a>{" "}
                instead and we&apos;ll add you.
              </p>
            )}
          </div>
        </form>
      </Reveal>
    </section>
  );
}
